import { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const getCartSessionId = () => {
  try {
    let sessionId = localStorage.getItem('cart_session_id');
    if (!sessionId) {
      sessionId = `guest_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('cart_session_id', sessionId);
    }
    return sessionId;
  } catch {
    return `guest_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
};

const getGuestWishlistProductIds = () => {
  try {
    const raw = localStorage.getItem('guestWishlist');
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((p) => p._id || p.id)
      .filter((id) => id && typeof id === 'string' && id.length === 24 && /^[0-9a-fA-F]{24}$/.test(id));
  } catch {
    return [];
  }
};

/**
 * After a registration timeout, silently try to log in with the
 * submitted credentials. If it succeeds, the user WAS created —
 * we just never got the response back. Return a success result so
 * the frontend can redirect normally instead of showing an error.
 */
async function recoverRegistrationAfterTimeout(email, password, sessionId) {
  try {
    const res = await fetch(`${API_URL}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-session-id': sessionId,
      },
      body: JSON.stringify({ email, password, rememberMe: true }),
    });
    const data = await res.json();
    if (data.success && data.token) {
      return { recovered: true, token: data.token, user: data.user, redirectPath: data.redirectPath || '/' };
    }
    return { recovered: false };
  } catch {
    return { recovered: false };
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() =>
    localStorage.getItem('token') || sessionStorage.getItem('token') || null
  );
  const isAuthLoading = false;

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    sessionStorage.removeItem('token');
    localStorage.removeItem('cart_items_count');
    localStorage.removeItem('cart_session_id');
    localStorage.removeItem('guestWishlist');
  };

  const getUser = async () => {
    if (!token || typeof token !== 'string' || token.trim() === '') {
      setLoading(false);
      return;
    }
    try {
      const response = await fetch(`${API_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${token.trim()}` },
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.user) setUser(data.user);
      } else if (response.status === 401) {
        logout();
      }
    } catch (error) {
      console.error('Network error fetching user — keeping session:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) { getUser(); } else { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlToken = urlParams.get('token');
    if (urlToken && !token) {
      setToken(urlToken);
      localStorage.setItem('token', urlToken);
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }
    const storedToken = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (storedToken && storedToken !== token) setToken(storedToken);
  }, [token]);

  useEffect(() => {
    const handleTokenStored = (event) => {
      const newToken = event.detail?.token || localStorage.getItem('token') || sessionStorage.getItem('token');
      if (newToken && newToken !== token) setToken(newToken);
    };
    window.addEventListener('tokenStored', handleTokenStored);
    return () => window.removeEventListener('tokenStored', handleTokenStored);
  }, [token]);

  // ─── REGISTER ────────────────────────────────────────────────────────────────
  //
  // KEY FIX: On Render free tier, the backend cold-start can take 30–60s.
  // The old code retried register() on timeout — which hit the backend AGAIN
  // after the first request already created the user, causing a duplicate-email
  // error or a confusing UX.
  //
  // New behaviour:
  //   1. One registration attempt only — no retries on timeout.
  //   2. On timeout (AbortError), silently attempt login with the same credentials.
  //   3. If login succeeds → user WAS created, we just missed the response.
  //      Treat it as a successful registration.
  //   4. If login also fails → user was NOT created, show a clean "try again" error.
  //
  const register = async (firstName, lastName, email, password) => {
    const controller  = new AbortController();
    // 150s: generous enough for Render cold start (usually 50–90s)
    const timeoutId   = setTimeout(() => controller.abort(), 150_000);
    const sessionId   = getCartSessionId();
    const guestWishlistProductIds = getGuestWishlistProductIds();

    try {
      const response = await fetch(`${API_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-session-id': sessionId,
        },
        body: JSON.stringify({
          firstName,
          lastName,
          email,
          password,
          ...(guestWishlistProductIds.length > 0 && { guestWishlistProductIds }),
        }),
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      const data = await response.json();

      if (data.success) {
        setToken(data.token);
        setUser(data.user);
        localStorage.setItem('token', data.token);
        localStorage.removeItem('guestWishlist');
        return { success: true, redirectPath: data.redirectPath || '/' };
      }

      return { success: false, message: data.message, code: data.code };

    } catch (error) {
      clearTimeout(timeoutId);

      // ── Timeout / network error recovery ──────────────────────────────────
      // The request may have reached the backend before the abort fired.
      // Try logging in — if it works, the user was created successfully.
      if (error.name === 'AbortError' || error.name === 'TypeError') {
        const recovery = await recoverRegistrationAfterTimeout(email, password, sessionId);

        if (recovery.recovered) {
          // ✅ User was created — just store credentials and proceed
          setToken(recovery.token);
          setUser(recovery.user);
          localStorage.setItem('token', recovery.token);
          localStorage.removeItem('guestWishlist');
          return { success: true, redirectPath: recovery.redirectPath };
        }

        // User was NOT created — safe to tell them to retry
        return {
          success: false,
          message: error.name === 'AbortError'
            ? 'The server is waking up (cold start). Please try again — it should be fast now.'
            : 'Network error. Please check your connection and try again.',
          code: error.name === 'AbortError' ? 'TIMEOUT_ERROR' : 'NETWORK_ERROR',
        };
      }

      return {
        success: false,
        message: 'Something went wrong. Please try again.',
        code: 'UNKNOWN_ERROR',
      };
    }
  };

  // ─── LOGIN ────────────────────────────────────────────────────────────────────
  const login = async (email, password, rememberMe = true) => {
    const controller = new AbortController();
    const timeoutId  = setTimeout(() => controller.abort(), 150_000);

    try {
      const sessionId = getCartSessionId();
      const guestWishlistProductIds = getGuestWishlistProductIds();
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-session-id': sessionId,
        },
        body: JSON.stringify({
          email,
          password,
          rememberMe,
          ...(guestWishlistProductIds.length > 0 && { guestWishlistProductIds }),
        }),
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      const data = await response.json();

      if (data.success) {
        setToken(data.token);
        setUser(data.user);
        rememberMe
          ? localStorage.setItem('token', data.token)
          : sessionStorage.setItem('token', data.token);
        localStorage.removeItem('guestWishlist');
        return { success: true, redirectPath: data.redirectPath || '/', user: data.user };
      }

      return { success: false, message: data.message, code: data.code };

    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        return {
          success: false,
          message: 'The server is waking up. Please try again in a moment.',
          code: 'TIMEOUT_ERROR',
        };
      }
      return { success: false, message: 'Network error. Please try again.', code: 'NETWORK_ERROR' };
    }
  };

  // ─── FORGOT PASSWORD ──────────────────────────────────────────────────────────
  const forgotPassword = async (email, attempt = 0) => {
    const controller = new AbortController();
    const timeoutId  = setTimeout(() => controller.abort(), 60_000);
    try {
      const response = await fetch(`${API_URL}/auth/forgotpassword`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      return await response.json();
    } catch (error) {
      clearTimeout(timeoutId);
      if (attempt < 2 && error.name !== 'AbortError') {
        await new Promise(r => setTimeout(r, Math.min(1000 * 2 ** attempt, 8000)));
        return forgotPassword(email, attempt + 1);
      }
      return {
        success: false,
        message: error.name === 'AbortError' ? 'Request timed out. Please try again.' : 'Network error. Please try again.',
        code: error.name === 'AbortError' ? 'TIMEOUT_ERROR' : 'NETWORK_ERROR',
      };
    }
  };

  // ─── RESET PASSWORD ───────────────────────────────────────────────────────────
  const resetPassword = async (token, password) => {
    try {
      const response = await fetch(`${API_URL}/auth/resetpassword/${token}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      const data = await response.json();
      return { success: data.success, message: data.message };
    } catch {
      return { success: false, message: 'Network error. Please try again.' };
    }
  };

  // ─── CHANGE PASSWORD ──────────────────────────────────────────────────────────
  const changePassword = async (currentPassword, newPassword) => {
    try {
      const t = localStorage.getItem('token');
      if (!t) return { success: false, message: 'Please sign in to change your password.', code: 'UNAUTHORIZED' };
      const response = await fetch(`${API_URL}/auth/me/password`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${t}` },
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      const data = await response.json();
      return response.ok
        ? { success: true,  message: data.message || 'Password changed successfully.' }
        : { success: false, message: data.message || 'Failed to change password.', code: data.code };
    } catch {
      return { success: false, message: 'Network error. Please try again.', code: 'NETWORK_ERROR' };
    }
  };

  // ─── GOOGLE LOGIN ─────────────────────────────────────────────────────────────
  const googleLogin = () => {
    const sessionId   = getCartSessionId();
    const currentPath = window.location.pathname + window.location.search || '/';
    let redirectPath  = currentPath;

    if (currentPath.includes('/login') || currentPath.includes('/register')) {
      const urlParams    = new URLSearchParams(window.location.search);
      const redirectParam = urlParams.get('redirect');
      redirectPath = redirectParam ? `/${redirectParam}` : '/';
    }

    try {
      localStorage.setItem('post_login_redirect', redirectPath);
    } catch { /* ignore */ }

    const url = new URL(`${API_URL}/auth/google`);
    url.searchParams.set('sessionId', sessionId);
    window.location.href = url.toString();
  };

  const value = {
    user,
    loading,
    token,
    isAuthLoading,
    register,
    login,
    logout,
    forgotPassword,
    resetPassword,
    changePassword,
    googleLogin,
    isAuthenticated: !!token && !!user,
    isTokenPresent:  !!token,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}